import type { VisibilityScopeValue } from "../../../components/ui/VisibilityScope";
import type { Item } from "../../types/items";
import { UNIT_SYSTEM_IMPERIAL, type UnitSystem } from "../../../types";
import type { SubstructureFamily, SubstructureFamilyForSettings } from "./substructureFamilies";
import type { SubstructureMaterial, SubstructureMaterialForSettings } from "./substructureMaterials";

// Same values as deck_designer.market_types.
export const SUBSTRUCTURE_COUNTRIES = ["eu", "us", "all"] as const;
export type SubstructureCountry = (typeof SUBSTRUCTURE_COUNTRIES)[number];

export type Substructure = {
  id: string;
  base: number | null;
  height: number;
  secondMomentOfArea: number | null;
  substructureFamily: SubstructureFamily | null;
  substructureMaterials: SubstructureMaterial[];
  item: Item | null;
  image: string | null;
  countries: SubstructureCountry[];
  visibility: VisibilityScopeValue;
  order: number;
};

// User side (settings, saved configuration): localized family and materials.
export type SubstructureForSettings = Omit<
  Substructure,
  "substructureFamily" | "substructureMaterials" | "visibility" | "order"
> & {
  // Settings only: set when the item is sent only because the calculation uses it (hidden since).
  onlyForCalculation?: boolean;
  substructureFamily: SubstructureFamilyForSettings | null;
  substructureMaterials: SubstructureMaterialForSettings[];
};

// Metric shows the European substructures, imperial the US ones; "all" ones are shown with both.
export const isSubstructureAvailableFor = (substructure: SubstructureForSettings, unitSystem: UnitSystem): boolean => {
  const country: SubstructureCountry = unitSystem === UNIT_SYSTEM_IMPERIAL ? "us" : "eu";
  return substructure.countries.some((option) => option === "all" || option === country);
};

export type SearchSubstructuresResponse = {
  items: Substructure[];
  total: number;
};

export type InsertSubstructureBody = {
  base: number | null;
  height: number;
  secondMomentOfArea: number | null;
  substructureFamilyCode: string | null;
  substructureMaterialCodes: string[];
  itemId: string | null;
  countries: SubstructureCountry[];
};

export type UpdateSubstructureBody = InsertSubstructureBody & {
  image?: string | null;
  removeImage?: boolean;
};
